import { sendGAEvent } from '@noths/polaris-client-google-analytics';
import { EventsMap } from '@noths/polaris-client-next-redux';
import { getProductCodeFromProductId, isBrowser } from '@noths/polaris-client-utils';

import { selectUserAppliedFilterTitles } from 'src/components/organisms/Filter/modules/selectors/selectors';
import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectTotalProducts } from 'src/redux/products/selectors';
import { actions as visibilityTrackingActions } from 'src/redux/visibilityTracking';
import { getImpressionFieldObject } from 'src/utils/googleAnalytics';
import { selectSelectedSortingOption } from '../../../components/organisms/FilterMenu/modules/selectors';
import { getProductImpressionsTrackingEvent } from '../../../tracking/events/productImpression';
import { ClearEcommerceObject } from '../../../types';
import { transformProductsToGAReturnedProductsArray } from '../../../utils/transformProductsToGAReturnedProductsArray';
import { PRODUCT_IMPRESSION_BATCH_SIZE, PRODUCT_IMPRESSION_TIMEOUT_MS } from '../constants';
import {
  selectCurrentPage,
  selectProductFromCode,
  selectProviderAttributionToken,
} from '../selectors';
import { requestProducts, updateFilters, UpdateFiltersActionType } from '../thunks';
import { TriggerName } from '../types';
import { FilterUpdateImpressionsEvent } from './events';
type RequestProductsAction = ReturnType<(typeof requestProducts)['fulfilled']>;
type UpdateFiltersAction = ReturnType<(typeof updateFilters)['fulfilled']>;

const sendProductImpressionsEvent = (
  productImpressionQueue: number[],
  state: ReduxApplicationState,
) => {
  const { items, productsReturnedCount, searchTerm, category } = state.products;
  const { pageType } = state.navigation;

  sendGAEvent<ClearEcommerceObject>({
    ecommerce: null,
  });

  const productImpressionsEvent = getProductImpressionsTrackingEvent({
    eventCategory: 'Product listings',
    eventAction: 'Product Impressions',
    eventLabel: 'Impressions',
    impressions: productImpressionQueue.map((productId) => {
      const product = selectProductFromCode(state, getProductCodeFromProductId(productId));

      return getImpressionFieldObject(
        product!,
        items,
        productsReturnedCount,
        category,
        searchTerm,
        pageType,
        {
          ...(product?.isExpandedSearch && { listSuffix: ' - expanded' }),
        },
      );
    }),
  });

  sendGAEvent(productImpressionsEvent);
};

export const GAEventsMap: EventsMap<ReduxApplicationState> = {
  [visibilityTrackingActions.sendProductImpressionBatch.type]: (prevState, _, __, dispatch) => {
    const { productImpressionQueue } = prevState.visibilityTracking;

    if (!isBrowser() || productImpressionQueue.length === 0) {
      return;
    }

    sendProductImpressionsEvent(productImpressionQueue, prevState);

    // Clear the impression queue in the store
    dispatch(visibilityTrackingActions.clearProductImpressionQueue());
  },
  [requestProducts.pending.type]: (_prevState, _nextState, _action, dispatch) => {
    dispatch(visibilityTrackingActions.sendProductImpressionBatch());
  },
  [visibilityTrackingActions.onProductVisible.type]: (prevState, nextState, _action, dispatch) => {
    const { productImpressionQueue } = nextState.visibilityTracking;

    if (productImpressionQueue.length === PRODUCT_IMPRESSION_BATCH_SIZE) {
      // We have reached the batch limit and should send the batch
      dispatch(visibilityTrackingActions.sendProductImpressionBatch());
      return;
    }

    if (!prevState.visibilityTracking.atLeastOneProductHasBecomeVisible) {
      // Send batch on an interval so we can ensure event is sent during inactivity
      setInterval(
        () => dispatch(visibilityTrackingActions.sendProductImpressionBatch()),
        PRODUCT_IMPRESSION_TIMEOUT_MS,
      );

      // Send batch if user navigates away from page
      window.onbeforeunload = () => {
        dispatch(visibilityTrackingActions.sendProductImpressionBatch());
        return; // Empty return needed to prevent dialog box showing before unload
      };
    }
  },
  [requestProducts.fulfilled.type]: (prevState, nextState, action) => {
    const { payload } = action as RequestProductsAction;
    if (!payload.data) {
      return;
    }

    const { triggerName, data } = payload;
    const { items, currentPage } = data;

    const paginationTriggerNames = [
      TriggerName.LOAD_MORE_PRODUCTS,
      TriggerName.LOAD_PREVIOUS_PRODUCTS,
    ];
    const paginationAction = triggerName && paginationTriggerNames.includes(triggerName);

    if (paginationAction) {
      const attributionToken = selectProviderAttributionToken(prevState);

      const productImpressionsEvent = getProductImpressionsTrackingEvent({
        eventAction:
          triggerName === TriggerName.LOAD_PREVIOUS_PRODUCTS
            ? 'Load Previous Impressions'
            : 'Load More Impressions',
        eventCategory: 'Product listings',
        eventLabel: String(selectCurrentPage(prevState)),
        ...(attributionToken && { attributionToken }),
        ...(items?.[currentPage] && {
          returnedProducts: transformProductsToGAReturnedProductsArray(items[currentPage]),
        }),
      });

      sendGAEvent(productImpressionsEvent);
    }
  },
  [updateFilters.fulfilled.type]: (state, _, action) => {
    const { payload } = action as UpdateFiltersAction;
    const {
      products: { items, currentPage },
    } = state;
    const attributionToken = selectProviderAttributionToken(state);
    const returnedProducts = items?.[currentPage]
      ? transformProductsToGAReturnedProductsArray(items[currentPage])
      : null;

    switch (payload) {
      case UpdateFiltersActionType.FILTER:
        const updatedTotalProducts = selectTotalProducts(state);
        const appliedFilterTitles = selectUserAppliedFilterTitles(state);

        sendGAEvent<FilterUpdateImpressionsEvent>({
          event: 'custom_event',
          event_category: 'Filters',
          event_action: 'applied filter info',
          event_label: appliedFilterTitles,
          total_listing_items: updatedTotalProducts,
          ...(attributionToken && { attribution_token: attributionToken }),
          ...(returnedProducts && { returned_products: returnedProducts }),
        });
        break;
      case UpdateFiltersActionType.SORT:
        const selectedSortingOption = selectSelectedSortingOption(state);

        sendGAEvent({
          event: 'custom_event',
          event_category: 'Sort Results',
          event_action: 'sort by',
          event_label: selectedSortingOption?.title,
          ...(attributionToken && { attribution_token: attributionToken }),
          ...(returnedProducts && { returned_products: returnedProducts }),
        });
        break;
    }
  },
};
